import React, { useMemo } from 'react';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import NvEmoji from 'shared/components/nv-emoji';
import NvIcon from 'shared/components/nv-icon';
import { useSelector } from 'react-redux';
import AiQuestionsModalLoader from 'lecture_pages/components/quiz/ai-questions-modal-loader';
import { getCourseAliases } from 'redux/selectors/course';
import AskNovoAIButton from './ask-novo-ai-button';
import { AskNovoAiState } from '../helpers';

interface InitialDrawerStateProps {
  formFields: string;
  firstName: string;
  readOnly?: boolean;
  setFormFields: React.Dispatch<React.SetStateAction<string>>;
  onSubmit: () => void;
}

const InitialDrawerState = ({
  formFields,
  setFormFields,
  onSubmit,
  firstName,
  readOnly,
}: InitialDrawerStateProps) => {
  const askNovoAi: AskNovoAiState = useSelector(
    (state) => state.app.askNovoAi,
  );
  const aliases = useSelector(getCourseAliases);
  const { helpOptions, recentQuestions = [] } = askNovoAi;

  const description = useMemo(() => {
    if (helpOptions?.hasAiEmbeddingInProgress) {
      return t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.EMBEDDING();
    }
    if (recentQuestions?.length) {
      return t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.WELCOME_BACK_DESCRIPTION();
    }
    return t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.DESCRIPTION(aliases?.courseAliases);
  }, [helpOptions?.hasAiEmbeddingInProgress, recentQuestions?.length]);

  return (
    <>
      <div className='novoai-greeting'>
        <NvEmoji icon='wave' size='small' />
        <span className='novoai-greeting-text'>
          {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.GREETING()}{' '}
          {firstName},
        </span>
      </div>
      <div className='novoai-description'>
        {description}
      </div>
      {helpOptions?.hasAiEmbeddingInProgress && (
        <div className='loading-icon'>
          <AiQuestionsModalLoader customTitle={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.PROCESSING()} />
        </div>
      )}
      {!helpOptions?.hasAiEmbeddingInProgress && (
        <>
          <NvTextArea
            className='novoai-input-wrapper'
            textareaClassName='novoai-input'
            maxLength={2000}
            value={formFields}
            disabled={helpOptions.hasAiEmbeddingInProgress}
            onChange={(e) => setFormFields(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && formFields.trim() && !helpOptions.hasAiEmbeddingInProgress) {
                e.preventDefault();
                onSubmit();
              }
            }}
            placeholder={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.PLACEHOLDER()}
          />
          {!!recentQuestions?.length && !readOnly && (
          <div className='recent-questions-container'>
            <p className='recent-questions-header'>{t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.RECENT_QUESTIONS()}</p>
            {recentQuestions?.map((question, index) => (
              <button type='button' className='recent-questions-wrapper' key={question.id} disabled={helpOptions.hasAiEmbeddingInProgress} onClick={() => setFormFields(question.questionText)} data-qa={`${config.pendo.aiLearning.recentQuestions}_${index + 1}`}>
                <NvIcon icon='help' size='xs-small' className='icon' />
                <span className='recent-questions-text'>
                  {question.questionText}
                </span>
              </button>
            ))}
          </div>
          )}
          <AskNovoAIButton disabled={!formFields.trim() || helpOptions.hasAiEmbeddingInProgress} onClick={onSubmit} isSmall={readOnly}>
            {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.BUTTON()}
          </AskNovoAIButton>
        </>
      )}
    </>
  );
};

export default InitialDrawerState;
